import { useState, useEffect } from "react";
import { Switch, Button, StringSelect } from "@tellescope/ui";
import { Select, Label, Input } from "@tellescope/design-system";
import { ColorField } from "./BlockEditor/shared/ColorField";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Settings,
  Plug,
  MessageCircle,
  Eye,
  EyeOff,
  Palette,
  PanelLeft,
  Type,
  Link as LinkIcon,
} from "lucide-react";
import {
  FONT_FAMILY_OPTIONS,
  type PortalSettings,
  type OpenLoopChatIntegration,
  type SeasonIntegration,
  type ZendeskIntegration,
  type Theming,
  type Layout,
} from "../../../shared/schema";
import type { VisibilityCondition } from "@tellescope/visibility-schema";
import { preloadCuratedFonts, parseFontFamilyFromUrl } from "../../../shared/hooks/useFontLoader";
import {
  useOpenLoopChatPreviewEnabled,
  useSetOpenLoopChatPreviewEnabled,
  useZendeskPreviewEnabled,
  useSetZendeskPreviewEnabled,
} from "../../../shared/store/portalSchemaStore";
import { useEnduserCurrentEnduser } from "../../../enduser/store/hooks";
import { VisibilitySection } from "./BlockEditor/shared/VisibilitySection";
import { NewFormsExperienceSection } from "./NewFormsExperienceSection";
import { passwordManagerIgnoreProps } from "./passwordManagerIgnore";

interface PortalSettingsEditorProps {
  settings: PortalSettings;
  onUpdateSettings: (updates: Partial<PortalSettings>) => void;
}

export const PortalSettingsEditor: React.FC<PortalSettingsEditorProps> = ({ settings, onUpdateSettings }) => {
  const { t } = useTranslation("portal");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLayoutExpanded, setIsLayoutExpanded] = useState(false);
  const [isThemingExpanded, setIsThemingExpanded] = useState(false);
  const [isIntegrationsExpanded, setIsIntegrationsExpanded] = useState(false);
  const [isOpenLoopChatExpanded, setIsOpenLoopChatExpanded] = useState(false);
  const [isOpenLoopVisibilityExpanded, setIsOpenLoopVisibilityExpanded] = useState(false);
  const [isSeasonExpanded, setIsSeasonExpanded] = useState(false);
  const [isZendeskExpanded, setIsZendeskExpanded] = useState(false);

  const isPreviewEnabled = useOpenLoopChatPreviewEnabled();
  const setPreviewEnabled = useSetOpenLoopChatPreviewEnabled();
  const isZendeskPreviewEnabled = useZendeskPreviewEnabled();
  const setZendeskPreviewEnabled = useSetZendeskPreviewEnabled();
  const currentEnduser = useEnduserCurrentEnduser();

  const openLoopChat = settings.integrations?.openLoopChat ?? { enabled: false };
  const season = settings.integrations?.season ?? {};
  const zendesk = settings.integrations?.zendesk ?? { enabled: false };
  const theming = settings.theming ?? {};
  const layout = settings.layout ?? {};

  const handleOpenLoopChatUpdate = (updates: Partial<OpenLoopChatIntegration>) => {
    const enablingOpenLoop = updates.enabled === true;
    onUpdateSettings({
      integrations: {
        ...settings.integrations,
        openLoopChat: { ...openLoopChat, ...updates },
        ...(enablingOpenLoop && { zendesk: { ...zendesk, enabled: false } }),
      },
    });
  };

  const handleOpenLoopChatVisibilityChange = (condition: VisibilityCondition | undefined) => {
    handleOpenLoopChatUpdate({ visibility: condition ? { condition } : undefined });
  };

  const handleZendeskUpdate = (updates: Partial<ZendeskIntegration>) => {
    const enablingZendesk = updates.enabled === true;
    onUpdateSettings({
      integrations: {
        ...settings.integrations,
        zendesk: { ...zendesk, ...updates },
        ...(enablingZendesk && { openLoopChat: { ...openLoopChat, enabled: false } }),
      },
    });
  };

  const handleSeasonUpdate = (updates: Partial<SeasonIntegration>) => {
    onUpdateSettings({
      integrations: {
        ...settings.integrations,
        season: {
          ...season,
          ...updates,
        },
      },
    });
  };

  const handleThemingUpdate = (updates: Partial<Theming>) => {
    onUpdateSettings({
      theming: {
        ...theming,
        ...updates,
      },
    });
  };

  const handleLayoutUpdate = (updates: Partial<Layout>) => {
    onUpdateSettings({
      layout: {
        ...layout,
        ...updates,
      },
    });
  };

  // Preload curated fonts when theming section is expanded for dropdown preview
  useEffect(() => {
    if (isThemingExpanded) {
      preloadCuratedFonts();
    }
  }, [isThemingExpanded]);

  // Determine if using a custom font (not in curated list)
  const isCustomFont = theming.fontFamily && !FONT_FAMILY_OPTIONS.some((opt) => opt.value === theming.fontFamily);
  const selectedFontValue = isCustomFont ? "custom" : theming.fontFamily || "system";

  return (
    <div className="space-y-2">
      {/* Collapsible header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Settings className="h-4 w-4 mr-1" />
          {t("Portal Settings")}
        </button>
      </div>

      {/* Settings content (when expanded) */}
      {isExpanded && (
        <div className="space-y-4 pl-5">
          {/* Login Description */}
          <div className="space-y-1">
            <Label htmlFor="portal-settings-search-description" className="text-sm font-medium">
              {t("Login Description")}
            </Label>
            <Input
              id="portal-settings-search-description"
              name="portal-settings-search-description"
              {...passwordManagerIgnoreProps}
              value={settings.loginDescription ?? ""}
              onChange={(e) => onUpdateSettings({ loginDescription: e.target.value || undefined })}
              placeholder={t("Default: Enter your email or phone number")}
              className="text-sm"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground">{t("Shown below the Sign In title on the login page")}</p>
          </div>

          {/* Login Input Label */}
          <div className="space-y-1">
            <Label htmlFor="portal-settings-search-input-label" className="text-sm font-medium">
              {t("Login Input Label")}
            </Label>
            <Input
              id="portal-settings-search-input-label"
              name="portal-settings-search-input-label"
              {...passwordManagerIgnoreProps}
              value={settings.loginInputLabel ?? ""}
              onChange={(e) => onUpdateSettings({ loginInputLabel: e.target.value || undefined })}
              placeholder={t("Default: Email or Phone")}
              className="text-sm"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">{t("Label for the email/phone input on the login page")}</p>
          </div>

          {/* Disable Landing Page */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableLandingPage" className="text-sm font-medium">
                {t("Disable Landing Page")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("Unauthenticated users will be redirected to login")}</p>
            </div>
            <Switch
              id="disableLandingPage"
              checked={settings.disableLandingPage ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableLandingPage: checked })}
            />
          </div>

          {/* Disable Registration */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableRegistration" className="text-sm font-medium">
                {t("Disable Registration")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("Registration page will redirect to login")}</p>
            </div>
            <Switch
              id="disableRegistration"
              checked={settings.disableRegistration ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableRegistration: checked })}
            />
          </div>

          {/* Disable SMS for MFA */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableSMSForMFA" className="text-sm font-medium">
                {t("Disable SMS for MFA")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("Only allow email for multi-factor authentication codes")}
              </p>
            </div>
            <Switch
              id="disableSMSForMFA"
              checked={settings.disableSMSForMFA ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableSMSForMFA: checked })}
            />
          </div>

          <NewFormsExperienceSection settings={settings} onUpdateSettings={onUpdateSettings} />

          {/* Layout Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsLayoutExpanded(!isLayoutExpanded)}
            >
              {isLayoutExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <PanelLeft className="h-4 w-4 mr-1" />
              {t("Layout")}
            </button>

            {isLayoutExpanded && (
              <div className="space-y-4 pl-5">
                <p className="text-xs text-muted-foreground">{t("Configure how the portal navigation is displayed")}</p>

                {/* Enable Sidebar Navigation Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enableSidebarNavigation" className="text-sm font-medium">
                      {t("Enable Sidebar Navigation")}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("Show a sidebar navigation on larger screens (desktop)")}
                    </p>
                  </div>
                  <Switch
                    id="enableSidebarNavigation"
                    checked={layout.enableSidebarNavigation ?? false}
                    onCheckedChange={(checked) => handleLayoutUpdate({ enableSidebarNavigation: checked })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Theming Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsThemingExpanded(!isThemingExpanded)}
            >
              {isThemingExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Palette className="h-4 w-4 mr-1" />
              {t("Theming")}
            </button>

            {isThemingExpanded && (
              <div className="space-y-4 pl-5">
                <p className="text-xs text-muted-foreground">{t("Customize the portal colors to match your brand")}</p>

                {/* Primary Color */}
                <div className="space-y-1">
                  <ColorField
                    label={t("Primary Color")}
                    value={theming.primaryColor}
                    onChange={(color) => handleThemingUpdate({ primaryColor: color })}
                  />
                  <p className="text-xs text-muted-foreground">{t("Used for buttons, links, and primary actions")}</p>
                </div>

                {/* Secondary Color */}
                <div className="space-y-1">
                  <ColorField
                    label={t("Secondary Color")}
                    value={theming.secondaryColor}
                    onChange={(color) => handleThemingUpdate({ secondaryColor: color })}
                  />
                  <p className="text-xs text-muted-foreground">{t("Used for secondary buttons and backgrounds")}</p>
                </div>

                {/* Text Color */}
                <div className="space-y-1">
                  <ColorField
                    label={t("Text Color")}
                    value={theming.textColor}
                    onChange={(color) => handleThemingUpdate({ textColor: color })}
                  />
                  <p className="text-xs text-muted-foreground">{t("Used for body text across the portal")}</p>
                </div>

                {/* Header Color */}
                <div className="space-y-1">
                  <ColorField
                    label={t("Header Color")}
                    value={theming.headerColor}
                    onChange={(color) => handleThemingUpdate({ headerColor: color })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("Used for the portal navigation header background")}
                  </p>
                </div>

                {/* Button Text Color */}
                <div className="space-y-1">
                  <ColorField
                    label={t("Button Text Color")}
                    value={theming.buttonTextColor}
                    onChange={(color) => handleThemingUpdate({ buttonTextColor: color })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("Text color on primary buttons. Useful when your primary color is dark.")}
                  </p>
                </div>

                {/* Border Radius */}
                <div className="space-y-2">
                  <StringSelect
                    label={t("Border Radius")}
                    value={theming.borderRadius ?? "default"}
                    onValueChange={(value) =>
                      handleThemingUpdate({
                        borderRadius: value === "default" ? undefined : (value as Theming["borderRadius"]),
                      })
                    }
                    options={[
                      { value: "square", label: t("Square") },
                      { value: "default", label: t("Default") },
                      { value: "rounded", label: t("Rounded") },
                    ]}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("Controls roundness of buttons, cards, and inputs")}
                  </p>
                </div>

                {/* Font Family */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Type className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">{t("Font Family")}</Label>
                  </div>
                  <Select
                    value={selectedFontValue}
                    onValueChange={(value) => {
                      if (value === "system") {
                        handleThemingUpdate({ fontFamily: undefined, customFontUrl: undefined });
                      } else if (value === "custom") {
                        handleThemingUpdate({ fontFamily: "custom" });
                      } else {
                        handleThemingUpdate({ fontFamily: value, customFontUrl: undefined });
                      }
                    }}
                  >
                    <Select.Trigger className="w-full">
                      <Select.Value placeholder={t("Select a font")} />
                    </Select.Trigger>
                    <Select.Content>
                      {FONT_FAMILY_OPTIONS.map((font) => (
                        <Select.Item
                          key={font.value}
                          value={font.value}
                          style={{
                            fontFamily: font.value === "system" ? undefined : `"${font.value}", sans-serif`,
                          }}
                        >
                          {font.label}
                        </Select.Item>
                      ))}
                      <Select.Item value="custom">{t("Custom")}</Select.Item>
                    </Select.Content>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("Choose a font from the list or use a custom Google Fonts URL")}
                  </p>

                  {/* Custom Font URL Input */}
                  {(selectedFontValue === "custom" || theming.customFontUrl) && (
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="portal-settings-search-custom-font-url" className="text-sm font-medium">
                        {t("Custom Font URL")}
                      </Label>
                      <Input
                        id="portal-settings-search-custom-font-url"
                        name="portal-settings-search-custom-font-url"
                        {...passwordManagerIgnoreProps}
                        value={theming.customFontUrl || ""}
                        onChange={(e) => {
                          const url = e.target.value || undefined;
                          const parsedFontFamily = url ? parseFontFamilyFromUrl(url) : undefined;
                          handleThemingUpdate({
                            customFontUrl: url,
                            // Auto-populate font family name if successfully parsed
                            ...(parsedFontFamily && { fontFamily: parsedFontFamily }),
                          });
                        }}
                        placeholder="https://fonts.googleapis.com/css2?family=..."
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("Paste a Google Fonts CSS URL for custom fonts")}
                      </p>
                      {theming.customFontUrl && (
                        <div className="space-y-2">
                          <Label htmlFor="portal-settings-search-font-family-name" className="text-sm font-medium">
                            {t("Font Family Name")}
                          </Label>
                          <Input
                            id="portal-settings-search-font-family-name"
                            name="portal-settings-search-font-family-name"
                            {...passwordManagerIgnoreProps}
                            value={theming.fontFamily === "custom" ? "" : theming.fontFamily || ""}
                            onChange={(e) => handleThemingUpdate({ fontFamily: e.target.value || undefined })}
                            placeholder="e.g., Bebas Neue"
                            className="text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            {t("Enter the exact font family name from your Google Fonts URL")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Per-Style Font Overrides */}
                <div className="space-y-3 pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground font-medium">{t("Per-Style Font Overrides")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "Override the font for specific text styles. Leave as 'Same as above' to use the Font Family above."
                    )}
                  </p>

                  {/* Heading Font */}
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t("Heading Font (H1–H4)")}</Label>
                    <Select
                      value={theming.headingFont ?? "system"}
                      onValueChange={(value) =>
                        handleThemingUpdate({ headingFont: value === "system" ? undefined : value })
                      }
                    >
                      <Select.Trigger className="w-full">
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value="system">{t("Same as above")}</Select.Item>
                        {FONT_FAMILY_OPTIONS.filter((f) => f.value !== "system").map((font) => (
                          <Select.Item
                            key={font.value}
                            value={font.value}
                            style={{ fontFamily: `"${font.value}", sans-serif` }}
                          >
                            {font.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  </div>

                  {/* Body Font */}
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t("Body Font")}</Label>
                    <Select
                      value={theming.bodyFont ?? "system"}
                      onValueChange={(value) =>
                        handleThemingUpdate({ bodyFont: value === "system" ? undefined : value })
                      }
                    >
                      <Select.Trigger className="w-full">
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value="system">{t("Same as above")}</Select.Item>
                        {FONT_FAMILY_OPTIONS.filter((f) => f.value !== "system").map((font) => (
                          <Select.Item
                            key={font.value}
                            value={font.value}
                            style={{ fontFamily: `"${font.value}", sans-serif` }}
                          >
                            {font.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Integrations Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsIntegrationsExpanded(!isIntegrationsExpanded)}
            >
              {isIntegrationsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Plug className="h-4 w-4 mr-1" />
              {t("Integrations")}
            </button>

            {isIntegrationsExpanded && (
              <div className="space-y-3 pl-5">
                {/* OpenLoop Chat Integration */}
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setIsOpenLoopChatExpanded(!isOpenLoopChatExpanded)}
                  >
                    {isOpenLoopChatExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <MessageCircle className="h-4 w-4 mr-1" />
                    {t("OpenLoop Chat")}
                  </button>

                  {isOpenLoopChatExpanded && (
                    <div className="space-y-4 pl-5">
                      {/* Enable Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="openLoopChatEnabled" className="text-sm font-medium">
                            {t("Enable OpenLoop Chat")}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {t("Show Amazon Connect chat widget on all portal pages")}
                          </p>
                        </div>
                        <Switch
                          id="openLoopChatEnabled"
                          checked={openLoopChat.enabled ?? false}
                          onCheckedChange={(checked) => handleOpenLoopChatUpdate({ enabled: checked })}
                        />
                      </div>

                      {/* Preview Toggle (only shown when enabled) */}
                      {openLoopChat.enabled && (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={isPreviewEnabled ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPreviewEnabled(!isPreviewEnabled)}
                            className="gap-2"
                          >
                            {isPreviewEnabled ? (
                              <>
                                <EyeOff className="h-4 w-4" />
                                {t("Hide Preview")}
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4" />
                                {t("Preview Widget")}
                              </>
                            )}
                          </Button>
                          {isPreviewEnabled && (
                            <span className="text-xs text-muted-foreground">{t("Widget is visible on this page")}</span>
                          )}
                        </div>
                      )}

                      {/* Note: targeting condition can't be previewed without a selected enduser */}
                      {openLoopChat.enabled && openLoopChat.visibility?.condition && !currentEnduser && (
                        <p className="text-xs text-muted-foreground">
                          {t("Select an enduser above to preview how targeting affects this widget.")}
                        </p>
                      )}

                      {/* Configuration fields (only shown when enabled) */}
                      {openLoopChat.enabled && (
                        <div className="space-y-4">
                          {/* Source URL */}
                          <div className="space-y-1">
                            <Label htmlFor="portal-settings-search-chat-script-url" className="text-sm font-medium">
                              {t("Amazon Connect Script URL")}
                            </Label>
                            <Input
                              id="portal-settings-search-chat-script-url"
                              name="portal-settings-search-chat-script-url"
                              {...passwordManagerIgnoreProps}
                              value={openLoopChat.source ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ source: e.target.value })}
                              placeholder="https://example.my.connect.aws/.../amazon-connect-chat-interface-client.js"
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("The URL to the Amazon Connect chat widget script")}
                            </p>
                          </div>

                          {/* Snippet ID */}
                          <div className="space-y-1">
                            <Label htmlFor="portal-settings-search-chat-snippet-id" className="text-sm font-medium">
                              {t("Snippet ID")}
                            </Label>
                            <Input
                              id="portal-settings-search-chat-snippet-id"
                              name="portal-settings-search-chat-snippet-id"
                              {...passwordManagerIgnoreProps}
                              value={openLoopChat.snippetId ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ snippetId: e.target.value })}
                              placeholder="QVFJREFIaFg3VVJ2dFRXb2IrUUdndUkvUzRJ..."
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Base64 encoded snippet ID from Amazon Connect")}
                            </p>
                          </div>

                          {/* Session ID */}
                          <div className="space-y-1">
                            <Label htmlFor="portal-settings-search-chat-session-id" className="text-sm font-medium">
                              {t("Session ID")}
                            </Label>
                            <Input
                              id="portal-settings-search-chat-session-id"
                              name="portal-settings-search-chat-session-id"
                              {...passwordManagerIgnoreProps}
                              value={openLoopChat.sessionId ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ sessionId: e.target.value })}
                              placeholder="c16e02ca-e4c7-4a5c-99ea-07b7daf80b4b"
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Unique session identifier for the chat widget")}
                            </p>
                          </div>

                          {/* Platform Name */}
                          <div className="space-y-1">
                            <Label htmlFor="portal-settings-search-chat-platform-name" className="text-sm font-medium">
                              {t("Platform Name")}
                            </Label>
                            <Input
                              id="portal-settings-search-chat-platform-name"
                              name="portal-settings-search-chat-platform-name"
                              {...passwordManagerIgnoreProps}
                              value={openLoopChat.platformName ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ platformName: e.target.value })}
                              placeholder="Your Platform Name"
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Platform name for chat routing (must be pre-configured in Amazon Connect)")}
                            </p>
                          </div>

                          {/* Branding Section */}
                          <div className="space-y-4 pt-4 border-t border-border">
                            <p className="text-sm font-medium text-muted-foreground">{t("Branding")}</p>

                            {/* Header Message */}
                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-header-message"
                                className="text-sm font-medium"
                              >
                                {t("Header Message")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-header-message"
                                name="portal-settings-search-chat-header-message"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.headerMessage ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ headerMessage: e.target.value })}
                                placeholder="Welcome!"
                                className="text-sm"
                                maxLength={50}
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Text displayed in the chat widget header (under the logo)")}
                              </p>
                            </div>

                            {/* Logo URL */}
                            <div className="space-y-1">
                              <Label htmlFor="portal-settings-search-chat-logo-url" className="text-sm font-medium">
                                {t("Logo URL")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-logo-url"
                                name="portal-settings-search-chat-logo-url"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.logoUrl ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ logoUrl: e.target.value || undefined })}
                                placeholder="https://example.com/logo.png"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("URL to a .png, .jpg, or .svg image for the chat widget header")}
                              </p>
                            </div>

                            {/* Logo Alt Text */}
                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-logo-alt-text"
                                className="text-sm font-medium"
                              >
                                {t("Logo Alt Text")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-logo-alt-text"
                                name="portal-settings-search-chat-logo-alt-text"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.logoAltText ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ logoAltText: e.target.value })}
                                placeholder="Company Logo"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Alternative text for the logo image (for accessibility)")}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-agent-display-name"
                                className="text-sm font-medium"
                              >
                                {t("Agent Display Name")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-agent-display-name"
                                name="portal-settings-search-chat-agent-display-name"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.agentDisplayName ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ agentDisplayName: e.target.value })}
                                placeholder={t("Patient Support Agent")}
                                className="text-sm"
                                maxLength={50}
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Name shown on live agent messages in the chat transcript")}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-supervisor-display-name"
                                className="text-sm font-medium"
                              >
                                {t("Supervisor Display Name")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-supervisor-display-name"
                                name="portal-settings-search-chat-supervisor-display-name"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.supervisorDisplayName ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ supervisorDisplayName: e.target.value })}
                                placeholder={t("Patient Support Supervisor")}
                                className="text-sm"
                                maxLength={50}
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Name shown on supervisor messages in the chat transcript")}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-automated-message-display-name"
                                className="text-sm font-medium"
                              >
                                {t("Automated Message Display Name")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-automated-message-display-name"
                                name="portal-settings-search-chat-automated-message-display-name"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.automatedMessageDisplayName ?? ""}
                                onChange={(e) =>
                                  handleOpenLoopChatUpdate({ automatedMessageDisplayName: e.target.value })
                                }
                                placeholder={t("Automated Assistant")}
                                className="text-sm"
                                maxLength={26}
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Name shown on automated messages from the chat flow or a bot")}
                              </p>
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="space-y-0.5">
                                <Label htmlFor="openLoopChatShowMessageIcons" className="text-sm font-medium">
                                  {t("Show Message Icons")}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {t("Display an icon next to agent and automated messages in the chat transcript")}
                                </p>
                              </div>
                              <Switch
                                id="openLoopChatShowMessageIcons"
                                checked={!openLoopChat.hideMessageIcons}
                                onCheckedChange={(checked) => handleOpenLoopChatUpdate({ hideMessageIcons: !checked })}
                              />
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-agent-icon-url"
                                className="text-sm font-medium"
                              >
                                {t("Agent Icon URL")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-agent-icon-url"
                                name="portal-settings-search-chat-agent-icon-url"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.agentIconUrl ?? ""}
                                onChange={(e) =>
                                  handleOpenLoopChatUpdate({ agentIconUrl: e.target.value || undefined })
                                }
                                placeholder="https://example.com/agent-icon.png"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t(
                                  "Public image URL for the icon next to live agent messages. Leave blank to use the default robot icon."
                                )}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="portal-settings-search-chat-automated-message-icon-url"
                                className="text-sm font-medium"
                              >
                                {t("Automated Message Icon URL")}
                              </Label>
                              <Input
                                id="portal-settings-search-chat-automated-message-icon-url"
                                name="portal-settings-search-chat-automated-message-icon-url"
                                {...passwordManagerIgnoreProps}
                                value={openLoopChat.automatedMessageIconUrl ?? ""}
                                onChange={(e) =>
                                  handleOpenLoopChatUpdate({ automatedMessageIconUrl: e.target.value || undefined })
                                }
                                placeholder="https://example.com/bot-icon.png"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t(
                                  "Public image URL for the icon next to automated messages. Leave blank to use the default robot icon."
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Visibility / Phased Rollout Section */}
                          <VisibilitySection
                            visibility={openLoopChat.visibility}
                            onChange={handleOpenLoopChatVisibilityChange}
                            open={isOpenLoopVisibilityExpanded}
                            onOpenChange={setIsOpenLoopVisibilityExpanded}
                            label={t("Chat Widget Visibility Conditions")}
                            description={t(
                              "Optionally limit the OpenLoop chat widget to specific endusers (e.g. for a phased rollout). Leave unset to show to everyone."
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Season Integration */}
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setIsSeasonExpanded(!isSeasonExpanded)}
                  >
                    {isSeasonExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {t("Season")}
                  </button>

                  {isSeasonExpanded && (
                    <div className="space-y-4 pl-5">
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "Configure the Season dietitian-portal integration. Reference these values from rich-text links via {{season.banner}} and {{season.token}}. The dietitian_portal_redirect_to query param is appended automatically when a redirect is pending."
                        )}
                      </p>

                      <div className="space-y-1">
                        <Label htmlFor="portal-settings-search-season-banner" className="text-sm font-medium">
                          {t("Partner Banner")}
                        </Label>
                        <Input
                          id="portal-settings-search-season-banner"
                          name="portal-settings-search-season-banner"
                          {...passwordManagerIgnoreProps}
                          value={season.banner ?? ""}
                          onChange={(e) => handleSeasonUpdate({ banner: e.target.value })}
                          placeholder="CUSTOMER_NAME"
                          className="text-sm font-mono"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t("Partner banner value (e.g. CUSTOMER_NAME) supplied by Season.")}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <Label
                          htmlFor="portal-settings-search-season-redirect-template"
                          className="text-sm font-medium"
                        >
                          {t("Redirect URL Template")}
                        </Label>
                        <Input
                          id="portal-settings-search-season-redirect-template"
                          name="portal-settings-search-season-redirect-template"
                          {...passwordManagerIgnoreProps}
                          value={season.redirectTemplate ?? ""}
                          onChange={(e) => handleSeasonUpdate({ redirectTemplate: e.target.value })}
                          placeholder="https://stage-dietitian.partner.com/patient/partner-redirect?banner={{season.banner}}&token={{season.token}}"
                          className="text-sm font-mono"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "URL the portal redirects patients to after authentication when a dietitian_portal_redirect_to value was captured."
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Zendesk Integration */}
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setIsZendeskExpanded(!isZendeskExpanded)}
                  >
                    {isZendeskExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <MessageCircle className="h-4 w-4 mr-1" />
                    {t("Zendesk")}
                  </button>

                  {isZendeskExpanded && (
                    <div className="space-y-4 pl-5">
                      {/* BAA notice */}
                      <p className="text-xs text-amber-600">
                        {t(
                          "For HIPAA-compliance, you must have a separate BAA in place with every 3rd-party integration that can access patient data. A BAA with Tellescope alone is not enough."
                        )}
                      </p>

                      {/* Enable Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="zendeskEnabled" className="text-sm font-medium">
                            {t("Enable Zendesk Chat")}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {t("Show Zendesk chat widget on all portal pages")}
                          </p>
                          {openLoopChat.enabled && !zendesk.enabled && (
                            <p className="text-xs text-amber-600">{t("Enabling Zendesk will disable OpenLoop Chat")}</p>
                          )}
                        </div>
                        <Switch
                          id="zendeskEnabled"
                          checked={zendesk.enabled ?? false}
                          onCheckedChange={(checked) => handleZendeskUpdate({ enabled: checked })}
                        />
                      </div>

                      {/* Preview Toggle (only shown when enabled) */}
                      {zendesk.enabled && (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={isZendeskPreviewEnabled ? "default" : "outline"}
                            size="sm"
                            onClick={() => setZendeskPreviewEnabled(!isZendeskPreviewEnabled)}
                            className="gap-2"
                          >
                            {isZendeskPreviewEnabled ? (
                              <>
                                <EyeOff className="h-4 w-4" />
                                {t("Hide Preview")}
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4" />
                                {t("Preview Widget")}
                              </>
                            )}
                          </Button>
                          {isZendeskPreviewEnabled && (
                            <span className="text-xs text-muted-foreground">{t("Widget is visible on this page")}</span>
                          )}
                        </div>
                      )}

                      {/* Configuration fields (only shown when enabled) */}
                      {zendesk.enabled && (
                        <div className="space-y-4">
                          {/* Snippet Key */}
                          <div className="space-y-1">
                            <Label htmlFor="portal-settings-search-zendesk-snippet-key" className="text-sm font-medium">
                              {t("Zendesk Snippet Key")}
                            </Label>
                            <Input
                              id="portal-settings-search-zendesk-snippet-key"
                              name="portal-settings-search-zendesk-snippet-key"
                              {...passwordManagerIgnoreProps}
                              value={zendesk.snippetKey ?? ""}
                              onChange={(e) => handleZendeskUpdate({ snippetKey: e.target.value })}
                              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("UUID from your Zendesk Web Widget script URL (the key= parameter)")}
                            </p>
                          </div>

                          {/* Widget Version */}
                          <div className="space-y-1">
                            <Label htmlFor="zendeskWidgetVersion" className="text-sm font-medium">
                              {t("Widget Version")}
                            </Label>
                            <StringSelect
                              value={zendesk.widgetVersion ?? ""}
                              onValueChange={(value) => {
                                if (value === "classic" || value === "messaging") {
                                  handleZendeskUpdate({ widgetVersion: value });
                                }
                              }}
                              options={[
                                { value: "messaging", label: t("Messaging (Current)") },
                                { value: "classic", label: t("Classic (Legacy — pre June 2023)") },
                              ]}
                            />
                            <p className="text-xs text-muted-foreground">{t("Select your Zendesk account type")}</p>
                          </div>

                          {/* JWT Authentication (Messaging only) */}
                          {zendesk.widgetVersion === "messaging" && (
                            <div className="space-y-4 pt-4 border-t border-border">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label htmlFor="zendeskAuthEnabled" className="text-sm font-medium">
                                    {t("Enable JWT Authentication")}
                                  </Label>
                                  <p className="text-xs text-muted-foreground">
                                    {t(
                                      "Authenticate patients with Zendesk using a signed JWT from your own signing endpoint, instead of leaving the widget unauthenticated"
                                    )}
                                  </p>
                                </div>
                                <Switch
                                  id="zendeskAuthEnabled"
                                  checked={zendesk.authEnabled ?? false}
                                  onCheckedChange={(checked) => handleZendeskUpdate({ authEnabled: checked })}
                                />
                              </div>

                              {zendesk.authEnabled && (
                                <div className="space-y-1">
                                  <Label
                                    htmlFor="portal-settings-search-zendesk-jwt-endpoint"
                                    className="text-sm font-medium"
                                  >
                                    {t("JWT Signing Endpoint URL")}
                                  </Label>
                                  <Input
                                    id="portal-settings-search-zendesk-jwt-endpoint"
                                    name="portal-settings-search-zendesk-jwt-endpoint"
                                    {...passwordManagerIgnoreProps}
                                    value={zendesk.jwtEndpoint ?? ""}
                                    onChange={(e) => handleZendeskUpdate({ jwtEndpoint: e.target.value || undefined })}
                                    placeholder="https://your-endpoint.example.com/zendesk-jwt"
                                    className="text-sm font-mono"
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    {t(
                                      "URL of your own server-side endpoint implementing the Tellescope Zendesk JWT contract: it receives { authToken }, resolves the patient's identity via the Tellescope API, and returns a signed { jwt }. This endpoint must be built and hosted by you. Must be HTTPS — patient session tokens will not be sent over an unencrypted connection."
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
